import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Select, Button, Empty, Tag, Space } from 'antd';
import { SearchOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { MALL_APPS } from '../mallMockData';
import { RECOMMEND_PACKAGES } from '../recommendMockData';
import { CostPrice } from './PackagePriceCells';

// 海外售价为区域定价（海外默认 USD + 国家覆盖），只读来源《套餐配置》
const formatRegionPrice = (regionPricing) => {
    if (!regionPricing || regionPricing.defaultUsd == null) return '';
    const count = (regionPricing.countryOverrides || []).filter(c => c && c.country).length;
    return `$${(regionPricing.defaultUsd / 100).toFixed(2)}${count ? `(${count}国覆盖)` : ''}`;
};

const getSubPrice = (pkg) => {
    if (pkg.isSubscription !== 1) return '';
    const arr = [];
    if (pkg.salePriceCny != null) arr.push(`国内 ¥${(pkg.salePriceCny / 100).toFixed(2)}`);
    const region = formatRegionPrice(pkg.subscriptionRegionPricing);
    if (region) arr.push(`海外 ${region}`);
    return arr.length ? `订阅价: ${arr.join(' / ')}` : '';
};
const getNoSubPrice = (pkg) => {
    if (pkg.isNoSubscription !== 1) return '';
    const arr = [];
    if (pkg.noSubscriptionPriceCny != null) arr.push(`国内 ¥${(pkg.noSubscriptionPriceCny / 100).toFixed(2)}`);
    const region = formatRegionPrice(pkg.noSubscriptionRegionPricing);
    if (region) arr.push(`海外 ${region}`);
    return arr.length ? `非订阅价: ${arr.join(' / ')}` : '';
};

/**
 * 推荐位漏斗 - 选择套餐弹窗（单选）
 * 真实项目：views/operation/recommended/components/SelectPackageDialog.vue
 */
const RecommendSelectPackageDialog = ({ open, onCancel, onConfirm }) => {
    const [filter, setFilter] = useState({ releaseArea: undefined, releaseApp: undefined, operationPosition: undefined, keyword: '' });
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (open) {
            setFilter({ releaseArea: undefined, releaseApp: undefined, operationPosition: undefined, keyword: '' });
            setSelected(null);
        }
    }, [open]);

    const list = useMemo(() => RECOMMEND_PACKAGES.filter(p => {
        if (filter.releaseArea && !(p.releaseArea || []).includes(filter.releaseArea)) return false;
        if (filter.releaseApp && !(p.releaseApps || []).includes(filter.releaseApp)) return false;
        if (filter.operationPosition && !(p.releaseOpsPosition || []).includes(filter.operationPosition)) return false;
        if (filter.keyword) {
            const kw = filter.keyword.trim().toLowerCase();
            if (!p.name.toLowerCase().includes(kw) && !p.packageId.toLowerCase().includes(kw)) return false;
        }
        return true;
    }), [filter]);

    const handleConfirm = () => {
        if (!selected) return;
        onConfirm(selected);
        onCancel();
    };

    return (
        <Modal
            title="选择套餐"
            open={open}
            onCancel={onCancel}
            width={900}
            maskClosable={false}
            destroyOnClose
            footer={
                <Space>
                    <Button icon={<CloseOutlined />} onClick={onCancel}>取消</Button>
                    <Button type="primary" disabled={!selected} icon={<CheckOutlined />} onClick={handleConfirm}>确定</Button>
                </Space>
            }
        >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Select placeholder="投放区域" allowClear style={{ width: 140 }} value={filter.releaseArea} onChange={v => setFilter(f => ({ ...f, releaseArea: v }))} options={[{ label: '国内', value: '1' }, { label: '海外', value: '2' }]} />
                <Select placeholder="投放APP" allowClear style={{ width: 140 }} value={filter.releaseApp} onChange={v => setFilter(f => ({ ...f, releaseApp: v }))} options={MALL_APPS.map(a => ({ label: a.appName, value: a.appCode }))} />
                <Select placeholder="投放运营位" allowClear style={{ width: 140 }} value={filter.operationPosition} onChange={v => setFilter(f => ({ ...f, operationPosition: v }))} options={[{ label: '商城位', value: '商城位' }, { label: '推荐位', value: '推荐位' }]} />
            </div>

            <Input
                style={{ marginTop: 12 }}
                placeholder="搜索套餐名称或ID"
                allowClear
                prefix={<SearchOutlined />}
                value={filter.keyword}
                onChange={e => setFilter(f => ({ ...f, keyword: e.target.value }))}
            />

            <div style={{ fontSize: 12, color: '#909399', margin: '12px 0 8px' }}>从套餐配置模块的套餐列表中选择（单选）</div>

            <div style={{ maxHeight: 400, overflowY: 'auto', borderTop: '1px solid #ebeef5' }}>
                {list.map(pkg => {
                    const isSelected = selected?.packageId === pkg.packageId;
                    return (
                        <div
                            key={pkg.packageId}
                            onClick={() => setSelected(pkg)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: 16,
                                borderBottom: '1px solid #ebeef5', cursor: 'pointer',
                                background: isSelected ? '#ecf5ff' : '#fff',
                            }}
                        >
                            <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${isSelected ? '#409eff' : '#dcdfe6'}`, flexShrink: 0, position: 'relative' }}>
                                {isSelected && <div style={{ position: 'absolute', top: 3, left: 3, width: 6, height: 6, borderRadius: '50%', background: '#409eff' }} />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#303133', marginBottom: 6 }}>{pkg.name}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#909399' }}>
                                    <Tag>{pkg.packageId}</Tag>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 4, fontSize: 12 }}>
                                    {getSubPrice(pkg) && <span style={{ color: '#409eff', fontWeight: 500 }}>{getSubPrice(pkg)}</span>}
                                    {getNoSubPrice(pkg) && <span style={{ color: '#409eff', fontWeight: 500 }}>{getNoSubPrice(pkg)}</span>}
                                </div>
                                <div style={{ marginTop: 4, fontSize: 12 }}>
                                    <span style={{ color: '#909399' }}>套餐底价：</span>
                                    <CostPrice pkg={pkg} inline />
                                </div>
                            </div>
                        </div>
                    );
                })}
                {list.length === 0 && <Empty description="暂无套餐数据" style={{ padding: 24 }} />}
            </div>
        </Modal>
    );
};

export default RecommendSelectPackageDialog;
